/**
 * Seeded shuffling for practice cards.
 *
 * Multiple-choice apps need option order to be *stable across re-renders of
 * the same card* (so the buttons don't jump while the user is reading) but
 * *different card to card* (so position can't be memorised instead of the
 * answer). Seeding on the card id gives both.
 *
 * Extracted from the einbuergerungstest CardFront when the vokabeln app needed
 * the same behaviour plus seeded sampling.
 */

/** Small, fast, deterministic PRNG. Same seed → same sequence. */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher-Yates using a seeded PRNG. Does not mutate the input. */
export function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const arr = [...items];
  const rand = mulberry32(seed);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Picks `count` items without replacement, deterministically for a given seed.
 *
 * Used to draw the two wrong options from a word's curated distractor pool.
 * Seeding on the card id means the same card shows the same three options
 * within a session, while different cards draw different subsets — the pool is
 * larger than what is shown precisely so a fixed trio can't be learned by
 * shape instead of by reading the word.
 */
export function seededSample<T>(items: readonly T[], count: number, seed: number): T[] {
  return seededShuffle(items, seed).slice(0, count);
}
